
import { defineComponent } from '@vue/composition-api';
import CartItem from './CartItem.vue';
import Icon from '../icons.vue';
import IllinoisSummary from './Summary.vue';
import IllinoisBetTypeSelector from './BetTypeSelector.vue';
import Actions from './Actions.vue';
import { removeParentheses } from '@/composables/utils';
import { cartErrorsMixin } from './cartErrorsMixin';
import IllinoisTabBar from '../TabBar.vue';
import IllinoisMyBets from '../MyBetsComponents/IllinoisMyBets.vue';
import IllinoisMyBetsCashout from './MyBetsCashout.vue';
import IllinoisBetInAcceptance from './BetInAcceptance.vue';

export default defineComponent({
  name: 'IllinoisCart',
  mixins: [cartErrorsMixin],
  components: {
    CartItem,
    Icon,
    IllinoisSummary,
    IllinoisBetTypeSelector,
    Actions,
    IllinoisTabBar,
    IllinoisMyBets,
    IllinoisMyBetsCashout,
    IllinoisBetInAcceptance
  },
  emits: ['toggle-cart'],
  methods: {
    removeParentheses,
    t(key: string) {
      return this.$t(key);
    },
    toggleCart() {
      this.$emit('toggle-cart');
    },
    handleOpenCartPending() {
      // Apri il carrello se non è già aperto
      if (!this.isCartOpen) {
        this.toggleCart();
      }
      // Attiva la tab pending
      this.activeTab = 'pending';
    },
    resetCart() {
      this.$store.dispatch('cart/resetCart');
    },
    isMultiOddsEvent(odd: any) {
      const key = (odd && odd.event_id) ?? (odd && odd.event_description);
      return (this.eventCounts[key] || 0) > 1;
    },
    async onMultipleAmountInput() {
      if (this.betAmount < 0) {
        this.betAmount = 0;
      }
      this.getBet._multiple_detail.amount = this.betAmount;
      await this.$store.dispatch('cart/refreshCart');
    },
    selectAllText(event: Event) {
      const target = event.target as HTMLInputElement;
      target.select();
    },
    onBetAmountInput(event: Event) {
      const target = event.target as HTMLInputElement;
      const value = target.value;
      // Permetti solo numeri e virgola decimale
      const numericValue = value.replace(/[^0-9,]/g, '');
      // Gestisci più virgole decimali
      const parts = numericValue.split(',');
      let cleanedValue = parts[0];
      if (parts.length > 1) {
        cleanedValue += ',' + parts.slice(1).join('').substring(0, 2);
      }
      target.value = cleanedValue;
      const numValue = parseFloat(cleanedValue.replace(',', '.')) || 0;
      if (this.getBet._multiple_detail) {
        this.getBet._multiple_detail.amount = numValue >= 0 ? numValue : 0;
      }
      this.$store.dispatch('cart/refreshCart');
    },
    async formatBetAmount() {
      const amount = this.getBet._multiple_detail?.amount || 0;
      const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
      if (this.getBet._multiple_detail) {
        this.getBet._multiple_detail.amount = rounded;
      }
      await this.$store.dispatch('cart/refreshCart');
    },
    async incMultiple() {
      const step = 0.5;
      this.betAmount = Math.max(0, +(this.betAmount || 0) + step);
      await this.$store.dispatch('cart/refreshCart');
    },
    async decMultiple() {
      const step = 0.5;
      this.betAmount = Math.max(0, +(this.betAmount || 0) - step);
      await this.$store.dispatch('cart/refreshCart');
    },
    removeSingleOdd() {
      if (this.singleOdd) {
        this.$store.dispatch('cart/removeOddFromCart', {
          unique_id: this.singleOdd.unique_id
        });
      }
    },
    getErrorDesc(message: string): string {
      const split = message.split('#');
      return split[0] + ' ' + this.t(split[1]);
    },
    onActiveTabChange(tab: string) {
      // Nascondi i bottoni durante la transizione
      this.showActions = false;
      this.showMyBetsLoadMore = false;
      this.showCashoutPagination = false;

      // Calcola la direzione basandosi sull'indice dei tab
      const tabValues = this.tabItems.map(
        (item: { value: string }) => item.value
      );
      const currentIndex = tabValues.indexOf(this.activeTab);
      const newIndex = tabValues.indexOf(tab);

      // Se il nuovo tab è a destra, il contenuto esce verso sinistra (direzione 'right')
      // Se il nuovo tab è a sinistra, il contenuto esce verso destra (direzione 'left')
      this.transitionDirection = newIndex > currentIndex ? 'right' : 'left';
      this.activeTab = tab;
    },
    getTabIndex(tabValue: string): number {
      const tabValues = this.tabItems.map(
        (item: { value: string }) => item.value
      );
      return tabValues.indexOf(tabValue);
    },
    // Hooks per animazione altezza tab
    onTabBeforeLeave(el: HTMLElement) {
      // Fissa l'altezza corrente prima che l'elemento esca
      const wrapper = this.$refs.tabContentWrapper as HTMLElement;
      if (wrapper) {
        this.tabContentHeight = wrapper.scrollHeight + 'px';
      }
    },
    onTabEnter(el: HTMLElement, done: () => void) {
      // Calcola la nuova altezza e anima
      this.$nextTick(() => {
        const newHeight = el.scrollHeight;
        // Forza un reflow per far partire la transizione
        void el.offsetHeight;
        this.tabContentHeight = newHeight + 'px';
        // Aspetta che la transizione finisca
        setTimeout(done, 200);
      });
    },
    onTabAfterEnter() {
      // Ripristina height: auto dopo l'animazione
      this.tabContentHeight = 'auto';
      // Mostra il bottone appropriato in base al tab
      if (this.activeTab === 'cart') {
        this.showActions = true;
        this.showMyBetsLoadMore = false;
        this.showCashoutPagination = false;
      } else if (this.activeTab === 'myBets') {
        this.showMyBetsLoadMore = true;
        this.showActions = false;
        this.showCashoutPagination = false;
      } else if (this.activeTab === 'cashout') {
        this.showCashoutPagination = true;
        this.showActions = false;
        this.showMyBetsLoadMore = false;
      } else if (this.activeTab === 'pending') {
        this.showActions = false;
        this.showMyBetsLoadMore = false;
        this.showCashoutPagination = false;
      } else {
        this.showActions = false;
        this.showMyBetsLoadMore = false;
        this.showCashoutPagination = false;
      }
    },
    openAllMyBets() {
      this.$store.dispatch('IllinoisSport/setOpenMyBetModal', true);
    },
    onMyBetsLoadingChange(loading: boolean) {
      this.myBetsLoading = loading;
    },
    onMyBetsLoaded(hasBets: boolean) {
      this.myBetsHasBets = hasBets;
    },
    // Cashout pagination handlers
    onCashoutLoadingChange(loading: boolean) {
      this.cashoutLoading = loading;
    },
    onCashoutPaginationChange(pagination: {
      currentPage: number;
      totalPages: number;
      canGoNext: boolean;
      canGoPrev: boolean;
      hasBets: boolean;
    }) {
      this.cashoutCurrentPage = pagination.currentPage;
      this.cashoutTotalPages = pagination.totalPages;
      this.cashoutCanGoNext = pagination.canGoNext;
      this.cashoutCanGoPrev = pagination.canGoPrev;
      this.cashoutHasBets = pagination.hasBets;
    },
    goToCashoutNextPage() {
      const cashoutRef = this.$refs.illinoisMyBetsCashout as any;
      if (cashoutRef && typeof cashoutRef.goToNextPage === 'function') {
        cashoutRef.goToNextPage();
      }
    },
    goToCashoutPrevPage() {
      const cashoutRef = this.$refs.illinoisMyBetsCashout as any;
      if (cashoutRef && typeof cashoutRef.goToPrevPage === 'function') {
        cashoutRef.goToPrevPage();
      }
    }
  },

  computed: {
    // Store getters per autenticazione e cashout
    getAuthenticated(): boolean {
      return this.$store.getters['user/getAuthenticated'];
    },
    getCashout(): boolean {
      return this.$store.getters['settings/getCashout'];
    },
    getCashoutLive(): boolean {
      return this.$store.getters['settings/getCashoutLive'];
    },
    getCashoutV2(): boolean {
      return this.$store.getters['settings/getCashoutV2'];
    },
    isTerminalUser(): boolean {
      return this.$store.getters['user/isTerminalUser'];
    },
    hasPendingBets(): boolean {
      const accepting = this.$store.getters['cart/getAcceptingBets'] || [];
      const accepted = this.$store.getters['cart/getAcceptedBets'] || [];
      const withChange =
        this.$store.getters['cart/getAcceptedBetWithChange'] || [];
      const rejected = this.$store.getters['cart/getRejectedBets'] || [];
      return (
        accepting.length > 0 ||
        accepted.length > 0 ||
        withChange.length > 0 ||
        rejected.length > 0
      );
    },
    oddsVariation: {
      get(): boolean {
        return this.$store.getters['cart/getOddsVariation'];
      },
      set(value: boolean) {
        this.$store.dispatch('cart/setOddsVariation', value);
      }
    },
    betType() {
      return this.$store.getters['cart/getBet'].type;
    },
    odds() {
      return this.$store.getters['cart/getBet'].odds;
    },
    oddsLength() {
      return this.odds.length || 0;
    },
    eventCounts() {
      const counts: Record<string, number> = {};
      const list = Array.isArray(this.odds) ? this.odds : [];
      for (const item of list as any[]) {
        const key = (item && item.event_id) ?? (item && item.event_description);
        if (!key) continue;
        counts[String(key)] = (counts[String(key)] || 0) + 1;
      }
      return counts;
    },
    getBet() {
      return this.$store.getters['cart/getBet'];
    },
    singleOdd() {
      return this.oddsLength === 1 ? this.odds[0] : null;
    },
    singleOddValue() {
      return this.singleOdd ? this.singleOdd.outcome_odd_value : '';
    },
    betAmount: {
      get(): number {
        const amount = this.getBet._multiple_detail?.amount || 0;
        return amount;
      },
      set(value: number) {
        if (this.getBet._multiple_detail) {
          this.getBet._multiple_detail.amount = value >= 0 ? value : 0;
        }
      }
    },
    formattedBetAmount: {
      get(): string {
        const amount = this.getBet._multiple_detail?.amount || 0;
        return amount.toFixed(2).replace('.', ',');
      },
      set(value: string) {
        const numValue = parseFloat(value.replace(',', '.')) || 0;
        if (this.getBet._multiple_detail) {
          this.getBet._multiple_detail.amount = numValue >= 0 ? numValue : 0;
        }
      }
    },
    getSettings() {
      return this.$store.getters['cart/getSettings'];
    },
    getOddsVariation(): boolean {
      return this.$store.getters['cart/getOddsVariation'];
    },
    showOddsUpdateMessage(): boolean {
      return this.$store.getters['sport/showOddsUpdateMessage'];
    },
    showCart(): boolean {
      return this.$store.getters['IllinoisSport/getShowCart'] || false;
    },
    oddsToUpdate(): any[] {
      return this.$store.getters['IllinoisSport/getOddsToUpdate'] || [];
    },
    isSingleOddUpdated(): boolean {
      if (!this.singleOdd) return false;
      const updateItem = this.oddsToUpdate.find(
        (item: any) => item.source === this.singleOdd.unique_id
      );
      return (
        updateItem &&
        (updateItem.reason === 'changed_odd_value' ||
          updateItem.reason === 'changed_multiplicity')
      );
    },
    isSingleOddDisabled(): boolean {
      if (!this.singleOdd) return false;
      const updateItem = this.oddsToUpdate.find(
        (item: any) => item.source === this.singleOdd.unique_id
      );
      return updateItem && updateItem.reason === 'disabled';
    },
    // Computed per determinare se mostrare il contenitore dei bottoni in basso
    shouldShowBottomButtons(): boolean {
      // Actions (Cart tab con odds)
      if (this.showActions) return true;
      // Load More (MyBets tab con bets caricate)
      if (
        this.activeTab === 'myBets' &&
        this.showMyBetsLoadMore &&
        !this.myBetsLoading &&
        this.myBetsHasBets
      )
        return true;
      // Pagination (Cashout tab con più di una pagina)
      if (
        this.activeTab === 'cashout' &&
        this.showCashoutPagination &&
        !this.cashoutLoading &&
        this.cashoutHasBets &&
        this.cashoutTotalPages > 1
      )
        return true;
      return false;
    },
    // Tab dinamici per TabBar
    tabItems(): Array<{ label: string; value: string; disabled?: boolean }> {
      const items: Array<{ label: string; value: string; disabled?: boolean }> =
        [];

      // Tab Pending - mostrato solo se ci sono bet in accettazione
      if (this.hasPendingBets) {
        items.push({ label: this.t('pending') as string, value: 'pending' });
      }

      items.push({ label: this.t('cart') as string, value: 'cart' });
      items.push({
        label: this.t('myBets') as string,
        value: 'myBets',
        disabled: this.isTerminalUser
      });

      if (
        this.getAuthenticated &&
        (this.getCashout || this.getCashoutLive || this.getCashoutV2)
      ) {
        items.push({
          label: this.t('bets.cashout') as string,
          value: 'cashout'
        });
      }

      return items;
    },
    // Classi di transizione dinamiche basate sulla direzione
    tabTransitionEnterClass(): string {
      // Se direzione 'right': nuovo contenuto entra da destra (translate-x positivo)
      // Se direzione 'left': nuovo contenuto entra da sinistra (translate-x negativo)
      return this.transitionDirection === 'right'
        ? 'translate-x-full opacity-0'
        : '-translate-x-full opacity-0';
    },
    tabTransitionLeaveClass(): string {
      // Se direzione 'right': contenuto vecchio esce verso sinistra (translate-x negativo)
      // Se direzione 'left': contenuto vecchio esce verso destra (translate-x positivo)
      return this.transitionDirection === 'right'
        ? '-translate-x-full opacity-0'
        : 'translate-x-full opacity-0';
    }
  },
  props: {
    isCartOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showOptions: false,
      activeTab: 'cart' as string,
      transitionDirection: 'right' as 'left' | 'right',
      tabContentHeight: 'auto' as string,
      showActions: false as boolean,
      showMyBetsLoadMore: false as boolean,
      cartJustOpened: false as boolean,
      cartClosing: false as boolean,
      myBetsLoading: true as boolean,
      myBetsHasBets: false as boolean,
      // Cashout pagination state
      showCashoutPagination: false as boolean,
      cashoutLoading: true as boolean,
      cashoutHasBets: false as boolean,
      cashoutCanGoNext: false as boolean,
      cashoutCanGoPrev: false as boolean,
      cashoutCurrentPage: 1 as number,
      cashoutTotalPages: 1 as number
    };
  },
  watch: {
    isCartOpen(newVal: boolean) {
      // Gestione animazione di apertura del carrello
      if (newVal) {
        this.cartJustOpened = true;
        // Rimuovi la classe dopo l'animazione
        setTimeout(() => {
          this.cartJustOpened = false;
        }, 300);
      }

      // Gestione animazione bottone Actions, Load More e Cashout Pagination
      if (newVal) {
        // Carrello aperto: mostra il bottone appropriato dopo che l'animazione di slide è completata (350ms)
        setTimeout(() => {
          if (this.activeTab === 'cart') {
            this.showActions = true;
            this.showMyBetsLoadMore = false;
            this.showCashoutPagination = false;
          } else if (this.activeTab === 'myBets') {
            this.showMyBetsLoadMore = true;
            this.showActions = false;
            this.showCashoutPagination = false;
          } else if (this.activeTab === 'cashout') {
            this.showCashoutPagination = true;
            this.showActions = false;
            this.showMyBetsLoadMore = false;
          } else if (this.activeTab === 'pending') {
            this.showActions = false;
            this.showMyBetsLoadMore = false;
            this.showCashoutPagination = false;
          } else {
            this.showActions = false;
            this.showMyBetsLoadMore = false;
            this.showCashoutPagination = false;
          }
        }, 250);
      } else {
        // Carrello chiuso: animazione di chiusura
        this.cartClosing = true;
        // Nascondi i bottoni immediatamente
        this.showActions = false;
        this.showMyBetsLoadMore = false;
        this.showCashoutPagination = false;
        // Rimuovi la classe dopo l'animazione di chiusura
        setTimeout(() => {
          this.cartClosing = false;
        }, 300);
      }
    }
  },
  mounted() {
    // Nessuna inizializzazione necessaria per il posizionamento con bottom
  },
  beforeDestroy() {
    this.$store.dispatch('cart/setIsCartOpen', false);
  }
});
